package com.rosario.juridico.ui.screens

import androidx.compose.runtime.*
import com.rosario.juridico.domain.AppMontos
import juridico5.composeapp.generated.resources.Res
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

data class NormativaPromesa(
    val titulo: String,
    val texto: String,
    val tags: List<String>
)

class PromesaViewModel {
    // Estado de la UI
    var valorUhomBase by mutableStateOf(AppMontos.UHOM_STR)
    var resultadoProceso by mutableStateOf("Con Acuerdo")
    
    // Resultados del cálculo
    var honorarioFinal by mutableStateOf(0.0)
    var mensajeLegal by mutableStateOf("")
    
    // Normativa para búsqueda
    var itemsNormativa by mutableStateOf<List<NormativaPromesa>>(emptyList())
    var searchQuery by mutableStateOf("")

    val filteredNormativa by derivedStateOf {
        val query = searchQuery.trim()
        if (query.isBlank()) {
            emptyList<NormativaPromesa>()
        } else {
            val normalizedQuery = query.normalizeForSearch()
            val tokens = normalizedQuery.split(" ").filter { it.isNotBlank() }
            
            itemsNormativa.filter { item ->
                // Normalizamos título, texto y tags para la comparación
                val combinedText = "${item.titulo} ${item.texto} ${item.tags.joinToString(" ")}"
                val normalizedContent = combinedText.normalizeForSearch()
                
                // El ítem debe contener TODOS los tokens buscados
                tokens.all { token -> normalizedContent.contains(token) }
            }
        }
    }

    private fun String.normalizeForSearch(): String {
        return this.lowercase()
            .replace('á', 'a').replace('é', 'e').replace('í', 'i')
            .replace('ó', 'o').replace('ú', 'u').replace('ü', 'u')
            .replace('ñ', 'n')
            .replace(Regex("[^a-z0-9 ]"), "")
    }

    fun cargarDatos(scope: CoroutineScope) {
        scope.launch {
            try {
                // Leer reglas
                val reglasContent = Res.readBytes("files/promesa_reglas_calculo.txt").decodeToString()
                parsearReglas(reglasContent)

                // Leer normativa
                val baseContent = Res.readBytes("files/decreto_379_2025_base.txt").decodeToString()
                itemsNormativa = parsearNormativa(baseContent)
            } catch (e: Exception) {
                e.printStackTrace()
            }
        }
    }

    private fun parsearReglas(content: String) {
        // En este caso, el prompt pide dejar incrementoAcuerdo expuesto con default 1.0
        content.lines().forEach { line ->
            // Si hubiera lógica de reglas externas se parsearía aquí
        }
    }

    private fun parsearNormativa(content: String): List<NormativaPromesa> {
        val list = mutableListOf<NormativaPromesa>()
        content.lines().forEach { line ->
            val l = line.trim()
            if (l.isEmpty() || l.startsWith("NORMATIVA_") || l.startsWith("[")) return@forEach 
            
            // Usamos '.-' como separador de título para el Decreto 379/2025
            val separator = if (l.contains(".-")) ".-" else ":"
            if (l.contains(separator)) {
                val parts = l.split(separator, limit = 2)
                val titulo = parts[0].trim()
                val resto = parts[1].trim()
                
                // Extraer tags para búsqueda fuzzy, pero ocultarlos en UI
                val tagMatch = "\\[Tags: (.*?)\\]".toRegex().find(resto)
                val tags = tagMatch?.groupValues?.get(1)
                    ?.split(",")
                    ?.map { it.trim().lowercase() } ?: emptyList()
                
                // Limpiar texto para la UI (eliminar tags del cuerpo)
                val textoSinTags = resto.replace("\\[Tags: .*?\\]".toRegex(), "").trim()
                
                list.add(NormativaPromesa(titulo, textoSinTags, tags))
            }
        }
        return list
    }

    fun calcular() {
        val base = valorUhomBase.toDoubleOrNull() ?: 0.0
        
        when (resultadoProceso) {
            "Con Acuerdo" -> {
                honorarioFinal = base * 28.0
                mensajeLegal = "28 UHOM - A cargo de la parte requerida"
            }
            "Sin Acuerdo" -> {
                honorarioFinal = base * 14.0
                mensajeLegal = "14 UHOM - Cubierto por el Fondo Estatal"
            }
            "Desistimiento Temprano" -> {
                honorarioFinal = base * 7.0
                mensajeLegal = "7 UHOM - A cargo de la parte requirente"
            }
        }
    }

    companion object {
        val instance = PromesaViewModel()
    }
}
